import { useEffect, useState, useRef } from "react";
import api from "../api/api";
import SignatureCanvas from "react-signature-canvas";

export default function OrdersInProgress() {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isPickupModal, setIsPickupModal] = useState(false);
  const [isSealModal, setIsSealModal] = useState(false);
  const [isSignatureModal, setIsSignatureModal] = useState(false);

  const [containerCount, setContainerCount] = useState(1);
  const [weight, setWeight] = useState("");
  const [remarks, setRemarks] = useState("");
  const [sealNumbers, setSealNumbers] = useState([]);
  const [email, setEmail] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [recipientName, setRecipientName] = useState("");

  const [errors, setErrors] = useState({
    containerCount: "",
    weight: "",
    email: "",
    sealNumbers: [],
    signature: "",
  });

  const sigCanvasRef = useRef(null);

  const isValidEmail = (value) => value && value.trim() !== "";

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await api.get("/orders/pickup");
      const filtered = res.data
        .filter((o) => Number(o.status_id) === 2)
        .sort((a, b) => {
          const dateA = new Date(a.service_date);
          const dateB = new Date(b.service_date);
          if (dateA - dateB !== 0) return dateA - dateB;
          return (a.priority || 0) - (b.priority || 0);
        });
      setOrders(filtered);
    } catch (err) {
      console.error(err);
    }
  };

  const openPickupModal = (order) => {
    setSelectedOrder(order);
    setContainerCount(1);
    setWeight("");
    setRemarks("");
    setEmail(order.email);
    setRecipientName("");
    setErrors({
      containerCount: "",
      weight: "",
      email: "",
      sealNumbers: [],
      signature: "",
    });
    setIsPickupModal(true);
  };

  const btn = {
    primary: "bg-blue-600 text-white text-sm font-semibold px-4 py-1.5 rounded hover:bg-blue-700 transition shadow-sm",
    success: "bg-green-600 text-white text-sm font-semibold px-4 py-1.5 rounded hover:bg-green-700 transition shadow-sm",
    danger: "bg-red-500 text-white text-sm font-semibold px-4 py-1.5 rounded hover:bg-red-600 transition shadow-sm",
    ghost: "bg-gray-200 text-gray-700 text-sm font-medium px-4 py-1.5 rounded hover:bg-gray-300 transition",
  };

  const handlePickupSave = () => {
    const newErrors = { containerCount: "", weight: "", email: "" };
    let hasError = false;

    if (!containerCount || containerCount <= 0) {
      newErrors.containerCount = "Ilość musi być większa niż 0";
      hasError = true;
    }
    if (!weight || Number(weight) <= 0) {
      newErrors.weight = "Waga musi być większa niż 0";
      hasError = true;
    }
    if (!email || !isValidEmail(email)) {
      newErrors.email = "Podaj adres(y) email";
      hasError = true;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    if (hasError) return;

    setSealNumbers(Array.from({ length: containerCount }, () => ""));
    setErrors((prev) => ({ ...prev, sealNumbers: Array(containerCount).fill("") }));
    setIsPickupModal(false);
    setIsSealModal(true);
  };

  const handleSealSave = () => {
    const sealErrors = sealNumbers.map((s) => (s.trim() === "" ? "Pole wymagane" : ""));
    setErrors((prev) => ({ ...prev, sealNumbers: sealErrors }));

    if (sealErrors.some((e) => e)) return;

    setIsSealModal(false);
    setIsSignatureModal(true);
  };

  const handleCancelSignature = () => {
    setIsSignatureModal(false);
    sigCanvasRef.current?.clear();
    setErrors((prev) => ({ ...prev, signature: "" }));
  };

  const handleSignatureSave = async () => {
    if (isSaving) return;

    if (sigCanvasRef.current.isEmpty()) {
      setErrors((prev) => ({ ...prev, signature: "Podpis jest wymagany" }));
      return;
    }

    if (!recipientName || recipientName.trim() === "") {
      setErrors((prev) => ({ ...prev, recipientName: "Imię i nazwisko odbiorcy jest wymagane" }));
      return;
    }

    setIsSaving(true);

    try {
      const dataUrl = sigCanvasRef.current.toDataURL();

      await api.put(`/contractors/${selectedOrder.contractor_id}`, { email });
      await api.put(`/orders/${selectedOrder.id}/pickup`, {
        picked_quantity: containerCount,
        picked_weight: weight,
        remarks,
      });
      await api.post("/order-lines", {
        order_id: selectedOrder.id,
        seal_numbers: sealNumbers,
      });
      await api.post("/signatures", {
        order_id: selectedOrder.id,
        image_data: dataUrl,
        signature_text: recipientName,
      });
      await api.put(`/document/send/protocol/${selectedOrder.id}`);

      setIsSignatureModal(false);
      fetchOrders();
    } catch (err) {
      console.error("Błąd zapisu: ", err.response?.data || err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    date.setDate(date.getDate() + 1);
    return date.toISOString().split("T")[0];
  };

  return (
    <div className="p-2 space-y-10">
      {orders.length === 0 ? (
        <div className="text-gray-600">
          Brak zaplanowanych zleceń do odbioru
        </div>
      ) : (
        <div className="space-y-10">
          {Object.entries(
            orders.reduce((acc, o) => {
              const key = o.service_date
                ? formatDate(o.service_date)
                : "Brak daty";

              if (!acc[key]) acc[key] = [];
              acc[key].push(o);
              return acc;
            }, {})
          ).map(([date, items]) => (
            <div
              key={date}
              className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                <h2 className="text-bg font-medium text-gray-600">
                  Data realizacji:{" "}
                  <span className="text-gray-800 font-bold">{date}</span>
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 text-gray-600 border-b border-gray-200">
                    <tr className="text-left text-sm font-semibold">
                      <th className="px-6 py-3 text-left">Nr</th>
                      <th className="px-6 py-3 text-left">Nazwa</th>
                      <th className="px-6 py-3 text-left">Miasto</th>
                      <th className="px-6 py-3 text-left">Ulica</th>
                      <th className="px-6 py-3 text-left">Kod</th>
                      <th className="px-6 py-3 text-left">Osoba kontaktowa</th>
                      <th className="px-6 py-3 text-left">Telefon</th>
                      <th className="px-6 py-3 text-left">Uwagi</th>
                      <th className="px-6 py-3 text-left">Ilość</th>
                      <th className="px-6 py-3 text-center">Akcje</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {items.map((o) => (
                      <tr key={o.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-3 text-gray-700">{o.id}</td>
                        <td className="px-6 py-3 font-medium text-gray-800">
                          {o.contractor_name}
                        </td>
                        <td className="px-6 py-3 text-gray-600">{o.city}</td>
                        <td className="px-6 py-3 text-gray-600">{o.street}</td>
                        <td className="px-6 py-3 text-gray-600">{o.zip_code}</td>
                        <td className="px-6 py-3 text-gray-600">{o.contact_person}</td>
                        <td className="px-6 py-3 text-gray-600">{o.phone}</td>
                        <td className="px-6 py-3 text-gray-600">
                          {o.pickup_remarks}
                        </td>
                        <td className="px-6 py-3 text-gray-700 font-medium">
                          {o.quantity}
                        </td>

                        <td className="px-6 py-3 text-center">
                          <button
                            onClick={() => openPickupModal(o)}
                            className={btn.primary}
                          >
                            Realizuj
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}


      {isPickupModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 w-[600px] rounded shadow-lg">
            <h2 className="text-xl font-bold mb-4">Realizuj - {selectedOrder.contractor_name}</h2>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                <label className="font-medium">Ilość:</label>
                <input
                  type="number"
                  min="1"
                  className={`border p-2 rounded ${errors.containerCount ? "border-red-500" : "border-gray-300"}`}
                  value={containerCount}
                  onChange={(e) => setContainerCount(parseInt(e.target.value))}
                />
                {errors.containerCount && <p className="text-red-500 text-sm">{errors.containerCount}</p>}
              </div>

              <div className="flex flex-col">
                <label className="font-medium">Waga:</label>
                <input
                  type="number"
                  className={`border p-2 rounded ${errors.weight ? "border-red-500" : "border-gray-300"}`}
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                />
                {errors.weight && <p className="text-red-500 text-sm">{errors.weight}</p>}
              </div>

              <div className="flex flex-col">
                <label className="font-medium">Email:</label>
                <input
                  type="email"
                  className={`border p-2 rounded ${errors.email ? "border-red-500" : "border-gray-300"}`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
              </div>

              <div className="flex flex-col">
                <label className="font-medium">Uwagi:</label>
                <textarea
                  className="border p-2 rounded"
                  rows={3}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button className={btn.danger} onClick={() => setIsPickupModal(false)}>Anuluj</button>
              <button className={btn.primary} onClick={handlePickupSave}>Dalej</button>
            </div>
          </div>
        </div>
      )}

      {isSealModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 w-[400px] rounded shadow-lg max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Pojemniki</h2>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-2 border">Pojemnik</th>
                  <th className="p-2 border">Nr plomby</th>
                </tr>
              </thead>
              <tbody>
                {sealNumbers.map((seal, i) => (
                  <tr key={i} className="border hover:bg-gray-50">
                    <td className="p-2 text-center">{i + 1}</td>
                    <td className="p-2">
                      <input
                        type="text"
                        className={`w-full border p-1 rounded ${errors.sealNumbers[i] ? "border-red-500" : "border-gray-300"}`}
                        value={seal}
                        onChange={(e) => {
                          const newSeals = [...sealNumbers];
                          newSeals[i] = e.target.value;
                          setSealNumbers(newSeals);

                          const newSealErrors = [...errors.sealNumbers];
                          newSealErrors[i] = e.target.value ? "" : "Pole wymagane";
                          setErrors((prev) => ({ ...prev, sealNumbers: newSealErrors }));
                        }}
                      />
                      {errors.sealNumbers[i] && <p className="text-red-500 text-sm">{errors.sealNumbers[i]}</p>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-end gap-3 mt-4">
              <button className={btn.danger} onClick={() => setIsSealModal(false)}>Anuluj</button>
              <button className={btn.primary} onClick={handleSealSave}>Dalej</button>
            </div>
          </div>
        </div>
      )}

      {isSignatureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 w-[800px] rounded shadow-lg space-y-4">
            <h2 className="text-xl font-bold mb-4">Elektroniczny podpis</h2>

            <SignatureCanvas
              ref={sigCanvasRef}
              penColor="black"
              canvasProps={{
                width: 750,
                height: 150,
                className: `border rounded ${errors.signature ? "border-red-500" : "border-gray-300"}`
              }}
            />
            {errors.signature && <p className="text-red-500 text-sm mt-1">{errors.signature}</p>}

            <div className="flex flex-col mt-2">
              <label className="font-medium">Imię i nazwisko odbiorcy:</label>
              <input
                type="text"
                className={`border p-2 rounded ${errors.recipientName ? "border-red-500" : "border-gray-300"}`}
                value={recipientName || ""}
                onChange={(e) => setRecipientName(e.target.value)}
              />
              {errors.recipientName && <p className="text-red-500 text-sm">{errors.recipientName}</p>}
            </div>

            <div className="flex justify-between mt-3">
              <button className={btn.ghost} onClick={() => sigCanvasRef.current.clear()}>Wyczyść</button>

              <div className="flex gap-3">
                <button className={btn.danger} onClick={handleCancelSignature}>Anuluj</button>
                <button className={btn.success} onClick={handleSignatureSave}>Zapisz</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}